import React from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../../components/custom/BackButton';
import ActionIcon from '../../../../components/custom/ActionIcon';
import { ChoiceEmployee, JobTitle } from '../../../../globals/interfaces';
import useSWR from 'swr';
import { customFetcher } from '../../../../utils';
import Loader from 'react-spinners/MoonLoader';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    jobTitle: JobTitle | undefined;
    deleteJob: () => Promise<void>;
}

export default function ModalDelete ({
    modal,
    setModal,
    jobTitle,
    deleteJob
}: Props) {

    const key = jobTitle ? 
        `/job-title-employees/${jobTitle.id}` : 
        null;

    const {
        data,
        isLoading
    } = useSWR(key, customFetcher<ChoiceEmployee>, {
        fallbackData: []
    });

    const enableDelete = !isLoading && !data.length;

    const toggle = () => setModal(!modal);

    if (!jobTitle) return null;

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Confirm Delete Job {jobTitle.jobId} {jobTitle.title}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <Body 
                    isLoading={isLoading}
                    data={data}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <FooterButtonsContainer>
                        <ActionIcon 
                            type={'check'}
                            disabled={!enableDelete} 
                            onClick={() => deleteJob()} 
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const Body = ({
    isLoading,
    data
}: {
    isLoading: boolean;
    data: Array<ChoiceEmployee>
}) => {
    if (isLoading) {
        return (
            <Loader />
        );
    }

    if (!data.length) {
        return (
            <h4>Are you sure you want to delete this job title?</h4>
        );
    }

    return (
        <div>
            <h4>This job title cannot be deleted as the following employees are still assigned to it - please unassign this job from all employees:</h4>
            <ol>
                {
                    data.map(({ s_first_name, s_last_name, s_email, s_home_station }, i) => (
                        <li key={i}>{s_first_name} {s_last_name} | {s_email} ({s_home_station})</li>
                    ))
                }
            </ol>
        </div>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{update?: boolean}>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;